import { Injectable, InjectionToken, inject } from '@angular/core';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { EMPTY, Observable } from 'rxjs';
import { AppUrlService } from '../services/app-url.service';
import { AUTH_CONFIG } from './auth.config';
import { matchesRedirectUri } from './redirect-uri';

export interface LoginLauncher {
  /**
   * The redirect URI this launch mechanism comes back through, and the single value the
   * whole authorization code flow has to agree on: it goes into the authorize request,
   * guards the incoming callback, and is repeated in the code exchange — where RFC 6749
   * §4.1.3 requires it to be identical to the one sent at authorize time.
   *
   * It belongs to the launcher because the mechanism that consumes the callback is what
   * decides where a callback can arrive at all. That also keeps the native/web decision in
   * a single place, the `LOGIN_LAUNCHER` factory below, instead of spreading a second
   * platform check across the config.
   */
  readonly redirectUri: string;
  open(authorizeUrl: string): Promise<void>;
  readonly callbackUrl$: Observable<string>;
  close(): Promise<void>;
  /**
   * Returns the OAuth callback URL if the app launch itself was the callback,
   * and cleans up the browser URL in the process. Otherwise, returns null.
   */
  consumeStartupCallback(): Promise<string | null>;
}

@Injectable()
export class CapacitorBrowserLauncher implements LoginLauncher {
  /** Every opened app URL; `AuthService` picks out the callback. */
  readonly callbackUrl$ = inject(AppUrlService).url$;

  /** The registered app link: the OS routes it into the app rather than into a browser. */
  readonly redirectUri = AUTH_CONFIG.redirectUri;

  async open(authorizeUrl: string): Promise<void> {
    await Browser.open({ url: authorizeUrl });
  }

  async close(): Promise<void> {
    await Browser.close().catch(() => undefined);
  }

  async consumeStartupCallback(): Promise<string | null> {
    return null;
  }
}

@Injectable()
export class CapacitorSystemBrowserLauncher implements LoginLauncher {
  readonly callbackUrl$ = inject(AppUrlService).url$;

  readonly redirectUri = AUTH_CONFIG.redirectUri;

  async open(authorizeUrl: string): Promise<void> {
    window.open(authorizeUrl, '_system');
  }

  async close(): Promise<void> {
    /* External system browser: nothing to close from inside the app */
  }

  async consumeStartupCallback(): Promise<string | null> {
    return null;
  }
}

export class RedirectLauncher implements LoginLauncher {
  readonly callbackUrl$ = EMPTY as Observable<string>;

  /**
   * The web deployment's own URL. Everything that is not a native build lands here — the
   * installed PWA as well as a plain browser tab, `ng serve` included — because all of
   * them return through a full-page redirect and none of them can follow an app link
   * without handing the login off to the installed native app.
   */
  readonly redirectUri = AUTH_CONFIG.pwaRedirectUri;

  async open(authorizeUrl: string): Promise<void> {
    window.location.assign(authorizeUrl);
  }

  async close(): Promise<void> {
    /* Page-Redirect: nothing to do here */
  }

  /**
   * After the full-page redirect, the callback (?code=…&state=…) is triggered as a page load.
   * It runs in APP_INITIALIZER, that is, before the router's initial navigation.
   */
  async consumeStartupCallback(): Promise<string | null> {
    const href = window.location.href;
    if (!matchesRedirectUri(href, this.redirectUri)) return null;
    const params = new URL(href).searchParams;
    if (!params.has('code') && !params.has('error')) return null;
    window.history.replaceState({}, '', '/');
    return href;
  }
}

export const LOGIN_LAUNCHER = new InjectionToken<LoginLauncher>('LOGIN_LAUNCHER', {
  providedIn: 'root',
  factory: () =>
    Capacitor.isNativePlatform()
      ? new CapacitorSystemBrowserLauncher()
      : new RedirectLauncher(),
});
